import React, { useState } from "react"
import { Alert, Spinner } from "reactstrap"
import { organizerLabel } from "./plannerLabels"
import { reviewCounts, reviewGroups, unavailableLines } from "./generationLabels"
import GenerationRow from "./GenerationRow"
import GapCells from "./GapCells"

// At most this many drafts in one create batch (the proposals module's MAX_BATCH).
export const MAX_BATCH = 600
const NO_FILTERS = { warnings: false, checkName: false, unticked: false, organizer: "", region: "", county: "" }
// The review's filters (ticket 17): With warnings, Check the name, Unticked, the organizer (a run by period), the region and the county.
const kept = (row, filters) => (!filters.warnings || row.warnings.length > 0) && (!filters.checkName || row.draft.checkName)
  && (!filters.unticked || !row.ticked) && (!filters.organizer || row.draft.organizer?.id === filters.organizer)
  && (!filters.region || row.draft.club?.region === filters.region) && (!filters.county || row.draft.club?.countyCode === filters.county)
// A filter's choices, from the rows: [value, label], sorted by label.
const choicesOf = (rows, pick) => [...new Map(rows.map(pick).filter(([value]) => value)).entries()].sort((a, b) => a[1].localeCompare(b[1]))

// The review before saving (ticket 17): the counts, the filters, the rows grouped by month, then the declined or withdrawn sources and the
// older seasons (unticked, with "Tick the whole group"), and the gaps beside them. Every change is the server's to recompute; nothing is
// saved before Save.
export default function GenerationReview({ answer, settings, timeZone, planner, updating, saving, canSave, onChoose, onSave, onDiscard }) {
  const [filters, setFilters] = useState(NO_FILTERS)
  const set = fields => setFilters(current => ({ ...current, ...fields }))
  const ticked = answer.rows.filter(row => row.ticked).length
  const shown = answer.rows.filter(row => kept(row, filters))
  const byPeriod = !answer.scope?.organizerId
  const bothSports = !answer.scope?.sport || [].concat(answer.scope.sport).length > 1
  const toggle = (key, label) => <button type="button" aria-pressed={filters[key]} className={"ep-chip" + (filters[key] ? " is-on" : "")}
    onClick={() => set({ [key]: !filters[key] })}>{label}</button>
  const select = (key, label, choices, empty) => <label className="ep-gen-filter">
    <span className="ep-label">{label}</span>
    <select className="form-control form-control-sm" aria-label={label} value={filters[key]} onChange={event => set({ [key]: event.target.value })}>
      <option value="">{empty}</option>
      {choices.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
    </select>
  </label>
  return <section className="ep-generate-review" aria-label="Generated drafts">
    <ul aria-label="Counts" className="ep-gen-counts">
      {reviewCounts(answer).map(count => <li key={count} className="ep-chip-static">{count}</li>)}
    </ul>
    {answer.unavailable?.length > 0 && <Alert color="warning" className="mb-0">
      {unavailableLines(answer.unavailable).map(text => <div key={text}>{text}</div>)}</Alert>}
    <div className="ep-filter-row" role="group" aria-label="Filter the drafts">
      {toggle("warnings", "With warnings")}
      {toggle("checkName", "Check the name")}
      {toggle("unticked", "Unticked")}
      {byPeriod && select("organizer", "Organizer", choicesOf(answer.rows, row => [row.draft.organizer?.id, organizerLabel(row.draft.organizer)]), "All organizers")}
      {select("region", "Region", choicesOf(answer.rows, row => [row.draft.club?.region, row.draft.club?.region]), "All regions")}
      {select("county", "County", choicesOf(answer.rows, row => [row.draft.club?.countyCode, row.draft.club?.county || row.draft.club?.countyCode]), "All counties")}
      {shown.length < answer.rows.length && <span className="ep-meta">{`${shown.length} of ${answer.rows.length} rows shown`}</span>}
    </div>
    {updating && <div className="ep-loading" role="status"><Spinner size="sm" /> Updating the drafts…</div>}
    <div className="ep-gen-layout">
      <div className="ep-card ep-gen-list">
        {reviewGroups(shown).map(group => <section key={group.key} aria-label={group.label} className="ep-gen-group">
          <div className="ep-gen-group-head">
            <h3>{group.label}</h3>
            {group.whole && <button type="button" className="btn btn-link btn-sm p-0" aria-label={`Tick the whole group: ${group.label}`}
              onClick={() => group.rows.filter(row => !row.ticked).forEach(row => onChoose(row.id, { ticked: true }))}>Tick the whole group</button>}
          </div>
          {group.rows.map(row => <GenerationRow key={row.id} row={row} settings={settings} timeZone={timeZone} onChoose={onChoose} />)}
        </section>)}
        {!shown.length && <p className="ep-meta mb-0">{answer.rows.length ? "No row matches these filters." : "No tournament of the source seasons fits this run."}</p>}
        <div className="ep-gen-save">
          <button type="button" className="btn btn-light" onClick={onDiscard}>Discard the drafts</button>
          <span className="ep-spacer" />
          {ticked > MAX_BATCH && <span className="text-danger">{`At most ${MAX_BATCH} drafts are saved at a time: untick some, or generate by organizer or by region.`}</span>}
          <button type="button" className="btn btn-primary" disabled={!ticked || ticked > MAX_BATCH || !canSave} onClick={onSave}>
            {saving ? "Saving…" : `Save ${ticked} draft${ticked === 1 ? "" : "s"}`}</button>
        </div>
      </div>
      <aside className="ep-card ep-gen-gaps" aria-label="Gaps">
        <b>Gaps</b>
        <span className="ep-meta">Information only: generation never makes a proposal for a gap.</span>
        <GapCells gaps={answer.gaps} planner={planner} bothSports={bothSports} />
      </aside>
    </div>
  </section>
}
